import { Component, inject, OnInit, HostListener } from '@angular/core';
import { CdfiProfileSummary } from '../../../my-profile/models/profile.model';
import { ProfileService } from '../../../my-profile/services/profile.service';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { takeUntil, catchError, of, forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { CurrencyPipe } from '@angular/common';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { SMEService } from '../../../sme/services/sme.service';
import { CompanyDetails } from '../../../../shared/models/company-details';
import { HttpErrorResponse } from '@angular/common/http';

export interface ActivityLogItem {
  title: string;
  description: string;
  timestamp: string;
  type: 'success' | 'info' | 'warning' | 'primary';
  icon: string;
  actionLabel?: string;
  actionRoute?: string;
}

@Component({
  selector: 'app-sme-dashboard',
  templateUrl: './sme-dashboard.component.html',
  styleUrls: ['./sme-dashboard.component.scss'],
  standalone: true,
  imports: [CurrencyPipe, SkeletonComponent],
})
export class SmeDashboardComponent extends DestroyBase implements OnInit {
  private profileService = inject(ProfileService);
  private smeService = inject(SMEService);
  private router = inject(Router);
  summary: CdfiProfileSummary | null = null;
  businessProfile: CompanyDetails | null = null;
  isLoading = false;
  activityLog: ActivityLogItem[] = [];

  ngOnInit() {
    this.loadDashboardData();
  }

  loadDashboardData(): void {
    this.isLoading = true;
    forkJoin({
      summary: this.profileService.getCdfiProfileSummary().pipe(
        catchError((err) => {
          console.error('Error loading profile summary', err);
          return of(null);
        }),
      ),
      businessProfile: this.smeService.getSMEBusinessRecord().pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status !== 404) {
            console.error('Error loading business profile', err);
          }
          return of(null);
        }),
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.summary = res.summary;
          this.businessProfile = res.businessProfile;
          this.generateActivityLog();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }

  get businessRecordsCount(): number {
    return this.businessProfile ? 1 : 0;
  }

  generateActivityLog(): void {
    const logs: ActivityLogItem[] = [];
    const hasProfile = this.businessRecordsCount > 0;

    // 1. Account Creation Milestone (Always success/completed)
    logs.push({
      title: 'Portal Account Verified',
      description:
        'Your user profile and portal access have been successfully verified and secured.',
      timestamp: 'Account active',
      type: 'success',
      icon: 'verified',
    });

    // 2. Profile Setup Milestone
    if (hasProfile) {
      logs.push({
        title: 'Business Profile Published',
        description:
          'Your business profile is live. Lenders can view your business info and details.',
        timestamp: 'Profile active',
        type: 'success',
        icon: 'business',
        actionLabel: 'View Profile',
        actionRoute: '/business',
      });
    } else {
      logs.push({
        title: 'Complete Business Profile',
        description:
          'Create your business profile to showcase your needs, industry, and goals to potential lenders.',
        timestamp: 'Action required',
        type: 'warning',
        icon: 'pending',
        actionLabel: 'Set Up Profile',
        actionRoute: '/business',
      });
    }

    // Stop adding post-setup milestones if profile doesn't exist yet
    if (!hasProfile) {
      this.activityLog = logs;
      return;
    }

    // 3. Matched Connections Check
    const matchedCount =
      this.businessProfile?.matched_record_connections?.length ?? 0;
    if (matchedCount > 0) {
      logs.push({
        title: `${matchedCount} Matched Organization${matchedCount === 1 ? '' : 's'} Found`,
        description: `Our system matched your business criteria with ${matchedCount} mission-aligned partner organization${matchedCount === 1 ? '' : 's'}.`,
        timestamp: 'Matched',
        type: 'success',
        icon: 'verified',
        actionLabel: 'View Profile',
        actionRoute: '/business',
      });
    }

    // 5. Views / Engagement (Dynamic count if views exist)
    const profileViews = this.summary?.profile_views?.cdfi_view_count ?? 0;
    const businessViews = this.summary?.business_views?.cdfi_view_count ?? 0;
    const totalViews = profileViews + businessViews;

    if (totalViews > 0) {
      logs.push({
        title: `You're Getting Noticed`,
        description: `Reviewed your profile ${profileViews} time${profileViews === 1 ? '' : 's'} and business info ${businessViews} time${businessViews === 1 ? '' : 's'}.`,
        timestamp: 'Updated recently',
        type: 'primary',
        icon: 'views',
      });
    }

    // 6. Assignees Check
    const assignees = this.businessProfile?.assignees ?? [];
    if (assignees.length > 0) {
      logs.push({
        title: `${assignees.length} Organization partner${assignees.length === 1 ? '' : 's'} assigned`,
        description: `${assignees.length} support lender${assignees.length === 1 ? '' : 's'} have been assigned to your requests. Check your assignments.`,
        timestamp: 'Active connection',
        type: 'success',
        icon: 'assignment',
        actionLabel: 'View Connections',
        actionRoute: '/business',
      });
    }

    // 7. Approved Loan Amount Check on Assignee
    const approvedLoanAssignees = assignees.filter(
      (a) =>
        a.approved_loan_amount !== undefined &&
        a.approved_loan_amount !== null &&
        a.approved_loan_amount > 0,
    );
    if (approvedLoanAssignees.length > 0) {
      const totalApprovedAmt = approvedLoanAssignees.reduce(
        (sum, a) => sum + (a.approved_loan_amount ?? 0),
        0,
      );
      logs.push({
        title: 'Funding Amount Approved',
        description: `Congratulations! Lenders have approved a total loan amount of $${totalApprovedAmt.toLocaleString()} for your business requests.`,
        timestamp: 'Milestone achieved',
        type: 'success',
        icon: 'dollar',
      });
    }

    this.activityLog = logs;
  }

  activeTooltip: string | null = null;

  toggleTooltip(id: string, event: Event): void {
    event.stopPropagation();
    this.activeTooltip = this.activeTooltip === id ? null : id;
  }

  closeAllTooltips(): void {
    this.activeTooltip = null;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeAllTooltips();
  }
}
